'use client';

import { useTranslation } from 'next-i18next';

import { FocusEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Input, useSlotRecipe } from '@chakra-ui/react';

import { useDebouncedCallback } from '@nocowanie/common-ui/hooks';

import { validatePageNumber } from '@app/helpers/pagination.helpers';

import { EllipsisInputProps } from './ellipsis-input.props';

export const EllipsisInput = ({ pageCount, onPageChange, ...boxProps }: EllipsisInputProps) => {
    const recipe = useSlotRecipe({ key: 'pagination' });
    const styles = recipe({});
    const { t } = useTranslation('listing', { keyPrefix: 'pagination' });
    const inputRef = useRef<HTMLInputElement>(null);

    const [isInputVisible, setIsInputVisible] = useState(false);
    const [jumpValue, setJumpValue] = useState<string>('');

    useEffect(() => {
        if (isInputVisible && inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [isInputVisible]);

    const handleJumpToPage = useCallback(() => {
        const validPage = validatePageNumber(jumpValue, pageCount);

        if (validPage !== null) {
            onPageChange(validPage);
        }

        setJumpValue('');
        setIsInputVisible(false);
    }, [jumpValue, pageCount, onPageChange]);

    const debouncedJumpToPage = useDebouncedCallback(handleJumpToPage, 100);

    const handleInputKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleJumpToPage();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setJumpValue('');
                setIsInputVisible(false);
            }
        },
        [handleJumpToPage],
    );

    const handleInputBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            debouncedJumpToPage();
        },
        [debouncedJumpToPage],
    );

    const handleInputChange = useCallback((value: string) => {
        if (value === '' || /^[0-9]+$/.test(value)) {
            setJumpValue(value);
        }
    }, []);

    const handleActivateInput = useCallback(() => {
        setIsInputVisible(true);
    }, []);

    const handlePlaceholderKeyDown = useCallback(
        (e: KeyboardEvent<HTMLElement>) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleActivateInput();
            }
        },
        [handleActivateInput],
    );

    return (
        <Box onMouseEnter={handleActivateInput} css={styles.ellipsisRoot} {...boxProps}>
            {isInputVisible ? (
                <Input
                    ref={inputRef}
                    value={jumpValue}
                    onChange={e => handleInputChange(e.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    placeholder={'nr'}
                    aria-label={t('jumpToPage')}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    css={styles.ellipsisInput}
                />
            ) : (
                <Box
                    as={'button'}
                    onClick={handleActivateInput}
                    onKeyDown={handlePlaceholderKeyDown}
                    aria-label={t('jumpToPage')}
                    css={styles.ellipsisPlaceholder}
                >
                    ...
                </Box>
            )}
        </Box>
    );
};
